import { Controller } from "@hotwired/stimulus";
import Chart from "chart.js/auto";

/**
 * Charts Controller with Dark Mode Support
 *
 * Features:
 * - Automatic dark/light theme detection
 * - Dynamic color scheme adaptation
 * - Theme change monitoring and chart updates
 * - Standardized color palette across all charts
 * - Improved accessibility and contrast ratios
 *
 * Theme Detection Methods:
 * 1. CSS class-based (.dark on html/body)
 * 2. Data attribute-based (data-theme="dark")
 * 3. System preference fallback (prefers-color-scheme: dark)
 */
export default class extends Controller {
  static targets = ["donationChart", "statusChart", "timeRange"];
  static values = {
    donationTrends: Array,
    donationStatus: Object,
  };

  // Color theme configuration
  get colorTheme() {
    const isDark = this.isDarkMode();

    return {
      // Chart data colors (consistent across light/dark modes)
      data: {
        successful: {
          primary: "rgb(16, 185, 129)", // emerald-500
          hover: "rgb(5, 150, 105)", // emerald-600
          background: isDark
            ? "rgba(16, 185, 129, 0.12)"
            : "rgba(16, 185, 129, 0.08)",
        },
        failed: {
          primary: "rgb(239, 68, 68)", // red-500
          hover: "rgb(220, 38, 38)", // red-600
          background: isDark
            ? "rgba(239, 68, 68, 0.12)"
            : "rgba(239, 68, 68, 0.08)",
        },
        pending: {
          primary: "rgb(2, 132, 199)", // blue-600
          hover: "rgb(3, 105, 161)", // blue-700
          background: isDark
            ? "rgba(2, 132, 199, 0.12)"
            : "rgba(2, 132, 199, 0.08)",
        },
        processing: {
          primary: "rgb(99, 102, 241)", // indigo-500
          hover: "rgb(79, 70, 229)", // indigo-600
          background: isDark
            ? "rgba(99, 102, 241, 0.12)"
            : "rgba(99, 102, 241, 0.08)",
        },
        abandoned: {
          primary: isDark ? "rgb(156, 163, 175)" : "rgb(156, 163, 175)", // gray-400
          hover: isDark ? "rgb(107, 114, 128)" : "rgb(107, 114, 128)", // gray-500
          background: isDark
            ? "rgba(156, 163, 175, 0.12)"
            : "rgba(156, 163, 175, 0.08)",
        },
      },
      // UI colors that adapt to theme
      ui: {
        background: isDark ? "rgb(17, 24, 39)" : "rgb(255, 255, 255)", // gray-900 : white
        surface: isDark ? "rgb(31, 41, 55)" : "rgb(249, 250, 251)", // gray-800 : gray-50
        border: isDark ? "rgb(75, 85, 99)" : "rgb(229, 231, 235)", // gray-600 : gray-200
        text: {
          primary: isDark ? "rgb(243, 244, 246)" : "rgb(17, 24, 39)", // gray-100 : gray-900
          secondary: isDark ? "rgb(156, 163, 175)" : "rgb(107, 114, 128)", // gray-400 : gray-500
          muted: isDark ? "rgb(107, 114, 128)" : "rgb(156, 163, 175)", // gray-500 : gray-400
        },
        grid: isDark ? "rgba(75, 85, 99, 0.2)" : "rgba(156, 163, 175, 0.15)", // gray-600 : gray-400
        tooltip: {
          background: isDark
            ? "rgba(17, 24, 39, 0.95)"
            : "rgba(255, 255, 255, 0.95)",
          border: isDark ? "rgba(75, 85, 99, 0.3)" : "rgba(156, 163, 175, 0.2)",
          text: isDark ? "rgb(243, 244, 246)" : "rgb(17, 24, 39)",
        },
        point: {
          border: isDark ? "rgb(31, 41, 55)" : "rgb(255, 255, 255)",
        },
      },
    };
  }

  // Detect if dark mode is active
  isDarkMode() {
    // Check for dark class on html or body element (common pattern)
    if (
      document.documentElement.classList.contains("dark") ||
      document.body.classList.contains("dark")
    ) {
      return true;
    }

    // Check for CSS custom property or data attribute
    if (document.documentElement.getAttribute("data-theme") === "dark") {
      return true;
    }

    // Fall back to system preference
    return (
      window.matchMedia &&
      window.matchMedia("(prefers-color-scheme: dark)").matches
    );
  }

  connect() {
    console.log("Charts controller connected");
    this.initializeCharts();
    this.restoreTimeRangeSelection();
    this.ensureInitialRangeConsistency();
    this.setupThemeWatcher();
  }

  disconnect() {
    this.destroyCharts();
    this.cleanupThemeWatcher();
  }

  // Setup theme change detection
  setupThemeWatcher() {
    // Watch for system theme changes
    this.mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    this.handleThemeChange = () => {
      console.log("Theme changed, refreshing charts");
      setTimeout(() => this.refreshChartsWithData(), 100); // Small delay to ensure DOM updates
    };

    this.mediaQuery.addEventListener("change", this.handleThemeChange);

    // Watch for manual theme toggles (common patterns)
    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === "attributes" &&
          (mutation.attributeName === "class" ||
            mutation.attributeName === "data-theme")
        ) {
          this.handleThemeChange();
        }
      });
    });

    this.observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class", "data-theme"],
    });

    this.observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });
  }

  // Cleanup theme watchers
  cleanupThemeWatcher() {
    if (this.mediaQuery && this.handleThemeChange) {
      this.mediaQuery.removeEventListener("change", this.handleThemeChange);
    }
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  // Initialize all charts
  initializeCharts() {
    try {
      this.initializeDonationChart();
      this.initializeDonationStatusChart();
    } catch (error) {
      console.error("Error initializing charts:", error);
    }
  }

  // Initialize donation trends chart
  initializeDonationChart() {
    if (!this.hasDonationChartTarget) {
      console.warn("Donation chart canvas not found");
      return;
    }

    const ctx = this.donationChartTarget.getContext("2d");
    const data = this.donationTrendsValue;
    const theme = this.colorTheme;

    // Destroy existing chart if it exists
    if (this.donationChart instanceof Chart) {
      this.donationChart.destroy();
    }

    this.donationChart = new Chart(ctx, {
      type: "line",
      data: {
        labels: data.map((d) => d.date),
        datasets: [
          {
            label: "Successful Donations",
            data: data.map((d) => d.successful_amount),
            borderColor: theme.data.successful.primary,
            backgroundColor: theme.data.successful.background,
            borderWidth: 3,
            pointRadius: 4,
            pointHoverRadius: 6,
            pointBackgroundColor: theme.data.successful.primary,
            pointBorderColor: theme.ui.point.border,
            pointBorderWidth: 2,
            pointHoverBackgroundColor: theme.data.successful.hover,
            pointHoverBorderColor: theme.ui.point.border,
            pointHoverBorderWidth: 3,
            tension: 0.4,
            fill: true,
            yAxisID: "y",
            datesFull: data.map((d) => d.date_full), // Store full dates for tooltips
          },
          {
            label: "Failed Donations",
            data: data.map((d) => d.failed_amount),
            borderColor: theme.data.failed.primary,
            backgroundColor: theme.data.failed.background,
            borderWidth: 3,
            pointRadius: 4,
            pointHoverRadius: 6,
            pointBackgroundColor: theme.data.failed.primary,
            pointBorderColor: theme.ui.point.border,
            pointBorderWidth: 2,
            pointHoverBackgroundColor: theme.data.failed.hover,
            pointHoverBorderColor: theme.ui.point.border,
            pointHoverBorderWidth: 3,
            tension: 0.4,
            fill: false,
            yAxisID: "y",
          },
          {
            label: "Pending Donations",
            data: data.map((d) => d.pending_amount),
            borderColor: theme.data.pending.primary,
            backgroundColor: theme.data.pending.background,
            borderWidth: 3,
            pointRadius: 4,
            pointHoverRadius: 6,
            pointBackgroundColor: theme.data.pending.primary,
            pointBorderColor: theme.ui.point.border,
            pointBorderWidth: 2,
            pointHoverBackgroundColor: theme.data.pending.hover,
            pointHoverBorderColor: theme.ui.point.border,
            pointHoverBorderWidth: 3,
            tension: 0.4,
            fill: false,
            yAxisID: "y",
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: "index",
          intersect: false,
        },
        plugins: {
          legend: {
            display: true,
            position: "top",
            labels: {
              usePointStyle: true,
              pointStyle: "circle",
              padding: 24,
              font: {
                size: 13,
                weight: "500",
              },
              color: theme.ui.text.secondary,
              generateLabels: function (chart) {
                const original =
                  Chart.defaults.plugins.legend.labels.generateLabels;
                const labels = original.call(this, chart);
                labels.forEach((label) => {
                  label.pointStyle = "circle";
                });
                return labels;
              },
            },
          },
          tooltip: {
            backgroundColor: theme.ui.tooltip.background,
            titleColor: theme.ui.tooltip.text,
            bodyColor: theme.ui.tooltip.text,
            borderColor: theme.ui.tooltip.border,
            borderWidth: 1,
            cornerRadius: 12,
            titleFont: {
              size: 14,
              weight: "600",
            },
            bodyFont: {
              size: 13,
              weight: "500",
            },
            padding: 16,
            displayColors: true,
            usePointStyle: true,
            callbacks: {
              title: function (context) {
                const index = context[0].dataIndex;
                const data = context[0].chart.data;
                // Try to get full date from the raw data if available
                return data.datasets[0].datesFull &&
                  data.datasets[0].datesFull[index]
                  ? data.datasets[0].datesFull[index]
                  : `${context[0].label}`;
              },
              label: function (context) {
                const label = context.dataset.label || "";
                return `${label}: GHS ${context.parsed.y.toLocaleString()}`;
              },
            },
          },
        },
        scales: {
          x: {
            display: true,
            grid: {
              display: false,
            },
            border: {
              display: false,
            },
            title: {
              display: true,
              text: "Timeline",
              font: {
                size: 13,
                weight: "600",
              },
              color: theme.ui.text.secondary,
            },
            ticks: {
              font: {
                size: 12,
                weight: "500",
              },
              color: theme.ui.text.secondary,
              maxTicksLimit: 8,
            },
          },
          y: {
            display: true,
            position: "left",
            grid: {
              color: theme.ui.grid,
              drawBorder: false,
            },
            border: {
              display: false,
            },
            title: {
              display: true,
              text: "Amount (GHS)",
              font: {
                size: 13,
                weight: "600",
              },
              color: theme.ui.text.secondary,
            },
            ticks: {
              font: {
                size: 12,
                weight: "500",
              },
              color: theme.ui.text.secondary,
              callback: function (value) {
                return "GHS " + value.toLocaleString();
              },
              padding: 8,
            },
          },
        },
        elements: {
          line: {
            borderJoinStyle: "round",
            borderCapStyle: "round",
          },
        },
      },
    });
  }

  // Initialize donation status chart
  initializeDonationStatusChart() {
    if (!this.hasStatusChartTarget) {
      console.warn("Donation status chart canvas not found");
      return;
    }

    const ctx = this.statusChartTarget.getContext("2d");
    const data = this.donationStatusValue;
    const theme = this.colorTheme;

    // Destroy existing chart if it exists
    if (this.statusChart instanceof Chart) {
      this.statusChart.destroy();
    }

    const chartData = [
      data.successful || 0,
      data.failed || 0,
      data.pending || 0,
      data.processing || 0,
      data.abandoned || 0,
    ];

    this.statusChart = new Chart(ctx, {
      type: "doughnut",
      data: {
        labels: ["Successful", "Failed", "Pending", "Processing", "Abandoned"],
        datasets: [
          {
            data: chartData,
            backgroundColor: [
              theme.data.successful.primary,
              theme.data.failed.primary,
              theme.data.pending.primary,
              theme.data.processing.primary,
              theme.data.abandoned.primary,
            ],
            borderWidth: 3,
            borderColor: theme.ui.point.border,
            hoverBorderWidth: 4,
            hoverBorderColor: theme.ui.point.border,
            hoverBackgroundColor: [
              theme.data.successful.hover,
              theme.data.failed.hover,
              theme.data.pending.hover,
              theme.data.processing.hover,
              theme.data.abandoned.hover,
            ],
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false, // We have custom legend in the view
          },
          tooltip: {
            backgroundColor: theme.ui.tooltip.background,
            titleColor: theme.ui.tooltip.text,
            bodyColor: theme.ui.tooltip.text,
            borderColor: theme.ui.tooltip.border,
            borderWidth: 1,
            cornerRadius: 12,
            titleFont: {
              size: 14,
              weight: "600",
            },
            bodyFont: {
              size: 13,
              weight: "500",
            },
            padding: 16,
            displayColors: true,
            usePointStyle: true,
            callbacks: {
              label: function (context) {
                const total = context.dataset.data.reduce((a, b) => a + b, 0);
                const percentage =
                  total > 0 ? ((context.raw / total) * 100).toFixed(1) : "0.0";
                return `${context.label}: ${context.raw} (${percentage}%)`;
              },
            },
          },
        },
        cutout: "65%", // Creates the doughnut hole - slightly larger for modern look
        elements: {
          arc: {
            borderRadius: 6, // Rounded corners for modern appearance
          },
        },
        animation: {
          animateScale: true,
          animateRotate: true,
        },
      },
    });
  } // Handle time range changes
  toggleTimeRange(event) {
    const range = event.target.value;
    console.log(`Changing time range to: ${range} days`);

    // Store the selection for persistence
    this.storeTimeRangeSelection(range);

    // Update the URL to reflect the new range
    this.updateUrlWithRange(range);

    this.updateChartsData(range);
  }

  // Update the URL with the selected range parameter
  updateUrlWithRange(range) {
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set("range", range);
    window.history.replaceState({}, "", currentUrl.toString());
  }

  // Update chart data for new time range
  updateChartsData(range) {
    console.log(`Updating charts for range: ${range} days`);

    // Show loading state
    this.showLoadingState();

    // Fetch new data
    fetch(`/admin_dashboard/trends?range=${range}.json`, {
      headers: {
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        this.updateDonationChart(data);
        this.hideLoadingState();
      })
      .catch((error) => {
        console.error("Error updating chart data:", error);
        this.hideLoadingState();
        this.showErrorState();
      });
  }

  // Update donation chart with new data
  updateDonationChart(data) {
    if (this.donationChart && data) {
      const theme = this.colorTheme;

      // Update chart data
      this.donationChart.data.labels = data.map((d) => d.date);

      // Update successful donations dataset
      this.donationChart.data.datasets[0].data = data.map(
        (d) => d.successful_amount
      );
      this.donationChart.data.datasets[0].datesFull = data.map(
        (d) => d.date_full
      ); // Update full dates for tooltips

      // Update failed donations dataset
      this.donationChart.data.datasets[1].data = data.map(
        (d) => d.failed_amount
      );

      // Update pending donations dataset
      this.donationChart.data.datasets[2].data = data.map(
        (d) => d.pending_amount
      );

      // Update colors to match current theme
      this.updateDonationChartColors();

      // Smooth animation update
      this.donationChart.update("resize");
    }
  }

  // Update donation chart colors based on current theme
  updateDonationChartColors() {
    if (!this.donationChart) return;

    const theme = this.colorTheme;
    const datasets = this.donationChart.data.datasets;

    // Update successful donations colors
    if (datasets[0]) {
      datasets[0].borderColor = theme.data.successful.primary;
      datasets[0].backgroundColor = theme.data.successful.background;
      datasets[0].pointBackgroundColor = theme.data.successful.primary;
      datasets[0].pointBorderColor = theme.ui.point.border;
      datasets[0].pointHoverBackgroundColor = theme.data.successful.hover;
      datasets[0].pointHoverBorderColor = theme.ui.point.border;
    }

    // Update failed donations colors
    if (datasets[1]) {
      datasets[1].borderColor = theme.data.failed.primary;
      datasets[1].backgroundColor = theme.data.failed.background;
      datasets[1].pointBackgroundColor = theme.data.failed.primary;
      datasets[1].pointBorderColor = theme.ui.point.border;
      datasets[1].pointHoverBackgroundColor = theme.data.failed.hover;
      datasets[1].pointHoverBorderColor = theme.ui.point.border;
    }

    // Update pending donations colors
    if (datasets[2]) {
      datasets[2].borderColor = theme.data.pending.primary;
      datasets[2].backgroundColor = theme.data.pending.background;
      datasets[2].pointBackgroundColor = theme.data.pending.primary;
      datasets[2].pointBorderColor = theme.ui.point.border;
      datasets[2].pointHoverBackgroundColor = theme.data.pending.hover;
      datasets[2].pointHoverBorderColor = theme.ui.point.border;
    }

    // Update chart options colors
    this.donationChart.options.plugins.legend.labels.color =
      theme.ui.text.secondary;
    this.donationChart.options.plugins.tooltip.backgroundColor =
      theme.ui.tooltip.background;
    this.donationChart.options.plugins.tooltip.titleColor =
      theme.ui.tooltip.text;
    this.donationChart.options.plugins.tooltip.bodyColor =
      theme.ui.tooltip.text;
    this.donationChart.options.plugins.tooltip.borderColor =
      theme.ui.tooltip.border;

    this.donationChart.options.scales.x.title.color = theme.ui.text.secondary;
    this.donationChart.options.scales.x.ticks.color = theme.ui.text.secondary;
    this.donationChart.options.scales.y.title.color = theme.ui.text.secondary;
    this.donationChart.options.scales.y.ticks.color = theme.ui.text.secondary;
    this.donationChart.options.scales.y.grid.color = theme.ui.grid;
  }

  // Restore the saved time range selection
  restoreTimeRangeSelection() {
    if (this.hasTimeRangeTarget) {
      const savedRange = sessionStorage.getItem("dashboard-time-range") || "30";
      this.timeRangeTarget.value = savedRange;

      // The initial data should already be correct based on URL params
      // Only update charts if needed for immediate visual feedback
      console.log(`Restored time range selection: ${savedRange} days`);
    }
  }

  // Store the selected time range
  storeTimeRangeSelection(range) {
    sessionStorage.setItem("dashboard-time-range", range);
  }

  // Show loading state on charts
  showLoadingState() {
    if (this.hasDonationChartTarget) {
      this.donationChartTarget.style.opacity = "0.6";
    }
    if (this.hasStatusChartTarget) {
      this.statusChartTarget.style.opacity = "0.6";
    }
  }

  // Hide loading state on charts
  hideLoadingState() {
    if (this.hasDonationChartTarget) {
      this.donationChartTarget.style.opacity = "1";
    }
    if (this.hasStatusChartTarget) {
      this.statusChartTarget.style.opacity = "1";
    }
  }

  // Show error state
  showErrorState() {
    console.warn("Failed to update chart data");
    // Could add visual error indicator here
  }

  // Refresh all charts
  refreshCharts() {
    console.log("Refreshing all charts");
    this.destroyCharts();
    this.initializeCharts();
  }

  // Refresh charts with fresh data from server
  refreshChartsWithData() {
    console.log("Refreshing charts with fresh data");
    const currentRange = this.getCurrentTimeRange();

    // Show loading state
    this.showLoadingState();

    // Fetch fresh data for both charts
    this.fetchChartData(currentRange)
      .then(({ donationTrends, donationStatus }) => {
        // Update the stimulus values with fresh data
        this.donationTrendsValue = donationTrends;
        this.donationStatusValue = donationStatus;

        // Refresh charts with new data
        this.destroyCharts();
        this.initializeCharts();

        // Restore time range selection
        this.setTimeRange(currentRange);

        this.hideLoadingState();
      })
      .catch((error) => {
        console.error("Error refreshing chart data:", error);
        this.hideLoadingState();
        this.showErrorState();
      });
  }

  // Get current time range from UI or fallback sources
  getCurrentTimeRange() {
    if (this.hasTimeRangeTarget) {
      return this.timeRangeTarget.value;
    }
    // Fallback to stored value or URL parameter
    const urlParams = new URLSearchParams(window.location.search);
    const urlRange = urlParams.get("range");
    const savedRange = sessionStorage.getItem("dashboard-time-range");
    return urlRange || savedRange || "30";
  }

  // Set time range in UI and storage
  setTimeRange(range) {
    console.log(`Setting time range to: ${range} days`);

    // Update the select element if it exists
    if (this.hasTimeRangeTarget) {
      this.timeRangeTarget.value = range;
    }

    // Store the selection for persistence
    this.storeTimeRangeSelection(range);

    // Update URL to reflect the current range
    this.updateUrlWithRange(range);
  }

  // Fetch chart data from server
  fetchChartData(range) {
    return fetch(`/admin_dashboard/trends?range=${range}.json`, {
      headers: {
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        // Return both donation trends and status data
        return {
          donationTrends: data.donation_trends || data.trends || data,
          donationStatus: data.donation_status || this.donationStatusValue,
        };
      });
  }

  // Update status chart colors based on current theme
  updateStatusChartColors() {
    if (!this.statusChart) return;

    const theme = this.colorTheme;
    const dataset = this.statusChart.data.datasets[0];

    if (dataset) {
      // Update colors
      dataset.backgroundColor = [
        theme.data.successful.primary,
        theme.data.failed.primary,
        theme.data.pending.primary,
        theme.data.processing.primary,
        theme.data.abandoned.primary,
      ];

      dataset.hoverBackgroundColor = [
        theme.data.successful.hover,
        theme.data.failed.hover,
        theme.data.pending.hover,
        theme.data.processing.hover,
        theme.data.abandoned.hover,
      ];

      dataset.borderColor = theme.ui.point.border;
      dataset.hoverBorderColor = theme.ui.point.border;
    }

    // Update tooltip colors
    this.statusChart.options.plugins.tooltip.backgroundColor =
      theme.ui.tooltip.background;
    this.statusChart.options.plugins.tooltip.titleColor = theme.ui.tooltip.text;
    this.statusChart.options.plugins.tooltip.bodyColor = theme.ui.tooltip.text;
    this.statusChart.options.plugins.tooltip.borderColor =
      theme.ui.tooltip.border;

    this.statusChart.update("none"); // Update without animation
  }

  // Get standardized color for a donation status
  getStatusColor(status, type = "primary") {
    const theme = this.colorTheme;
    const statusMap = {
      successful: theme.data.successful,
      failed: theme.data.failed,
      pending: theme.data.pending,
      processing: theme.data.processing,
      abandoned: theme.data.abandoned,
    };

    return statusMap[status]?.[type] || theme.data.abandoned[type];
  }

  // Destroy charts when controller disconnects
  destroyCharts() {
    if (this.donationChart instanceof Chart) {
      this.donationChart.destroy();
      this.donationChart = null;
    }
    if (this.statusChart instanceof Chart) {
      this.statusChart.destroy();
      this.statusChart = null;
    }
  }

  // Ensure initial page load respects user's saved time range preference
  ensureInitialRangeConsistency() {
    const savedRange = sessionStorage.getItem("dashboard-time-range") || "30";
    const currentUrl = new URL(window.location.href);
    const urlRange = currentUrl.searchParams.get("range") || "30";

    // If URL doesn't match saved preference, update URL and reload
    if (savedRange !== urlRange) {
      console.log(`Updating URL range from ${urlRange} to ${savedRange}`);
      currentUrl.searchParams.set("range", savedRange);
      window.location.href = currentUrl.toString();
    }
  }

  // Helper method to check if Chart.js is loaded
  get isChartJsLoaded() {
    return typeof Chart !== "undefined";
  }
}
